import type { ContainerModules } from '@yandex-int/k-common/typings';
import cx from 'classnames';
import groupBy from 'lodash/groupBy';
import { useGeneratePath, useStaticUrls } from 'platform-components';
import qs from 'query-string';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { SEARCH_OPEN_MODULE, SEARCH_SCROLL_TO_LESSON } from 'common.components/constants';
import { getCurrentYearCourseId } from 'common.components/course/selectors';

import { CourseContainerLessons } from '../__lessons/course-container__lessons';
import { CourseContainerModuleCardCarousel } from '../__module-card-carousel/course-container__module-card-carousel';
import { actions } from '../actions';
import { COURSE_CONTAINER_ANIMATION_TIMEOUT } from '../constants';
import { getExpandedLessonGroup, getOpenedModule } from '../selectors';

import './course-container__modules.scss';

interface Props {
    modules: ContainerModules;
}

export const useCourseContainerModuleIndexes = (modules: ContainerModules, moduleId: number | null) => {
    const groups = useMemo(() => Object.entries(groupBy(modules.cmodules || [], 'group_name')), [modules.cmodules]);

    const openedModule = useMemo(() => modules.cmodules.find((cm) => cm.module.id === moduleId), [modules, moduleId]);

    const openedModuleOrder = useMemo(
        () => groups.map((group) => group[0]).indexOf(openedModule?.group_name || '') + 1,
        [groups, openedModule?.group_name]
    );

    const openModuleOrderFromActiveGroup = useMemo(() => {
        const activeModuleGroup = modules.cmodules.filter((module) => module.group_name === openedModule?.group_name);
        return activeModuleGroup.map((group) => group.module.id).indexOf(moduleId || 0) + 1;
    }, [modules.cmodules, openedModule?.group_name, moduleId]);

    return { openedModule, openedModuleOrder, openModuleOrderFromActiveGroup };
};

export const CourseContainerModules = ({ modules }: Props) => {
    const openedModuleId = useSelector(getOpenedModule);
    const staticUrls = useStaticUrls();
    const getRouterPath = useGeneratePath();
    const { subject, containerSlug } = useParams<Record<string, string>>();
    const params = useParams<Record<string, string>>();

    const pathPartDependentOnTheRole = params.classId
        ? staticUrls.labLibrarySubjectCollection
        : staticUrls.labLibrarySubjectCommonCourseContainer;

    const libraryCollectionPath = getRouterPath(pathPartDependentOnTheRole, {
        containerSlug,
        subject,
    });

    const { openedModule, openedModuleOrder, openModuleOrderFromActiveGroup } = useCourseContainerModuleIndexes(
        modules,
        openedModuleId
    );

    const openModuleIndex = modules.cmodules.findIndex((module) => module.module.id === openedModuleId);

    const prevOpenedModule = useMemo(
        () => (modules.cmodules.length > 1 ? modules.cmodules[openModuleIndex - 1] : undefined),
        [modules, openModuleIndex]
    );

    const prevModuleLink = `${libraryCollectionPath}?${qs.stringify({
        [SEARCH_OPEN_MODULE]: prevOpenedModule?.module.id,
    })}`;

    const nextOpenedModule = useMemo(
        () => (modules.cmodules.length >= openModuleIndex ? modules.cmodules[openModuleIndex + 1] : undefined),
        [modules, openModuleIndex]
    );

    const nextModuleLink = `${libraryCollectionPath}?${qs.stringify({
        [SEARCH_OPEN_MODULE]: nextOpenedModule?.module.id,
    })}`;

    useRefreshLessonGroupOnModuleChange();

    if (!openedModule) {
        return null;
    }

    return (
        <div className={'course-container__modules-animation-wrapper'}>
            <TransitionGroup component={null}>
                <CSSTransition
                    classNames={cx('course-container__modules', 'course-container__modules_state')}
                    key={openedModule.module.id}
                    timeout={COURSE_CONTAINER_ANIMATION_TIMEOUT}
                >
                    <div className="course-container__modules-wrapper">
                        <CourseContainerModuleCardCarousel
                            containerModule={openedModule}
                            nextModule={nextOpenedModule}
                            nextModuleLink={nextModuleLink}
                            openModuleOrder={openedModuleOrder}
                            openModuleOrderFromActiveGroup={openModuleOrderFromActiveGroup}
                            prevModule={prevOpenedModule}
                            prevModuleLink={prevModuleLink}
                        />
                        <CourseContainerLessons containerModule={openedModule} />
                    </div>
                </CSSTransition>
            </TransitionGroup>
        </div>
    );
};

function useRefreshLessonGroupOnModuleChange() {
    const { search } = useLocation();
    const params = qs.parse(search);
    const searchLessonId = params[SEARCH_SCROLL_TO_LESSON];
    const dispatch = useDispatch();
    const courseId = useSelector(getCurrentYearCourseId)!;
    const openedModuleId = useSelector(getOpenedModule);
    const expandedLessonGroup = useSelector(getExpandedLessonGroup);

    useEffect(() => {
        if (expandedLessonGroup && searchLessonId === undefined) {
            dispatch(actions.setExpandedLessonGroupId({ lessonGroupId: null }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId, openedModuleId, dispatch]);
}
